import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, Filter, Search, X, Calendar, Flag } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { TOAST_MESSAGES, CONFIRM_MESSAGES } from '../utils/toastMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

const TodoCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterPriority, setFilterPriority] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilter, setColumnFilter] = useState('');
  const [executives, setExecutives] = useState([]);
  const [expandedColumn, setExpandedColumn] = useState('today');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const { success, error: showError } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    notes: [],
    priority: 'medium',
    category_bucket: 'backlog',
    due_date: '',
    executive_id: '',
    tags: []
  });
  const [newNoteText, setNewNoteText] = useState('');
  const [error, setError] = useState('');

  // Trello-style status-based columns
  const statusColumns = [
    { id: 'todo', label: 'To Do', color: '#3B82F6', icon: '📋' },
    { id: 'in_progress', label: 'In Progress', color: '#F59E0B', icon: '🔄' },
    { id: 'waiting', label: 'Waiting', color: '#8B5CF6', icon: '⏳' },
    { id: 'completed', label: 'Completed', color: '#10B981', icon: '✅' }
  ];
  
  // Legacy bucket columns (for backward compatibility / planning view)
  const bucketColumns = [
    { id: 'today', label: 'Today', color: '#3B82F6' },
    { id: 'tomorrow', label: 'Tomorrow', color: '#8B5CF6' },
    { id: 'this_week', label: 'This Week', color: '#10B981' },
    { id: 'next_week', label: 'Next Week', color: '#F59E0B' },
    { id: 'backlog', label: 'Backlog', color: '#6B7280' }
  ];
  
  // View mode: 'status' for Trello-style, 'bucket' for planning view
  const [viewMode, setViewMode] = useState('status');
  const columns = viewMode === 'status' ? statusColumns : bucketColumns;

  useEffect(() => {
    fetchTasks();
    fetchExecutives();
    
    // Check if navigated from dashboard with filter
    if (location.state?.filter) {
      setColumnFilter(location.state.filter);
    }
    
    // Handle window resize for mobile detection
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [location.state]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/assistant/tasks`, {
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      } else if (res.status === 401) {
        navigate('/assistant-login');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setLoading(false);
    }
  };

  const fetchExecutives = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/executives`, {
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setExecutives(data.executives || []);
      }
    } catch (err) {
      console.error('Error fetching executives:', err);
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside a valid droppable
    if (!destination) {
      return;
    }

    // Dropped in the same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Find the task being moved
    const taskId = draggableId;
    const newValue = destination.droppableId;
    
    // Determine which field to update based on view mode
    const updateField = viewMode === 'status' ? 'status' : 'category_bucket';

    // Optimistically update UI
    const updatedTasks = tasks.map(task =>
      task.id === taskId ? { ...task, [updateField]: newValue } : task
    );
    setTasks(updatedTasks);

    // Update backend
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({ [updateField]: newValue })
      });

      if (!res.ok) {
        // Revert on error
        setTasks(tasks);
        console.error('Failed to update task');
        showError('Update Failed', 'Could not update task status. Please try again.');
      } else {
        // Show success toast for status changes
        if (viewMode === 'status') {
          const newColumn = statusColumns.find(c => c.id === newValue);
          success('Task Moved', `Task moved to "${newColumn?.label || newValue}"`);
        }
      }
    } catch (err) {
      // Revert on error
      setTasks(tasks);
      console.error('Error updating task:', err);
      showError('Error', 'Failed to update task. Please try again.');
    }
  };

  const handleCreate = async () => {
    setError('');
    
    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        executive_id: formData.executive_id || null
      };

      const res = await fetch(`${BACKEND_URL}/api/assistant/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });

      if (res.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => navigate('/assistant-login'), 2000);
        return;
      }

      if (!res.ok) {
        if (res.status === 500) {
          setError('Server error. Please try again or contact admin.');
        } else if (res.status === 403) {
          setError('Unauthorized. Please check your permissions.');
        } else {
          setError(`Error ${res.status}: Failed to create task`);
        }
        return;
      }

      const data = await res.json();
      
      if (data.ok) {
        setShowCreateModal(false);
        setFormData({
          title: '',
          description: '',
          priority: 'medium',
          category_bucket: 'backlog',
          due_date: '',
          executive_id: '',
          tags: []
        });
        fetchTasks();
        success(TOAST_MESSAGES.TASK_CREATED.title, TOAST_MESSAGES.TASK_CREATED.message);
      } else {
        setError(data.message || 'Failed to create task');
      }
    } catch (err) {
      console.error('Task creation error:', err);
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
  };

  const handleEdit = async () => {
    setError('');
    
    if (!selectedTask) return;

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        notes: formData.notes || [],
        priority: formData.priority,
        category_bucket: formData.category_bucket,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null
      };

      const res = await fetch(`${BACKEND_URL}/api/assistant/tasks/${selectedTask.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      
      if (data.ok) {
        setShowEditModal(false);
        setSelectedTask(null);
        fetchTasks();
      } else {
        setError(data.message || 'Failed to update task');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleDeleteClick = () => {
    setTaskToDelete(selectedTask);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    
    setIsDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/tasks/${taskToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        setShowDeleteConfirm(false);
        setShowEditModal(false);
        fetchTasks();
        success(TOAST_MESSAGES.TASK_DELETED.title, TOAST_MESSAGES.TASK_DELETED.message);
      } else {
        showError(TOAST_MESSAGES.TASK_SAVE_ERROR.title, TOAST_MESSAGES.TASK_SAVE_ERROR.message);
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
    setIsDeleting(false);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        fetchTasks();
      }
    } catch (err) {
      console.error('Error updating task status:', err);
    }
  };

  const handleColumnChange = async (taskId, newColumn) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ category_bucket: newColumn })
      });

      if (res.ok) {
        fetchTasks();
      }
    } catch (err) {
      console.error('Error moving task:', err);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filterPriority && task.priority !== filterPriority) return false;
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    
    // Handle overdue filter specially
    if (columnFilter === 'overdue') {
      // Show overdue tasks (tasks with due_date in the past and not completed)
      if (!task.due_date || task.status === 'completed') return false;
      const dueDate = new Date(task.due_date);
      const now = new Date();
      return dueDate < now;
    }
    
    if (viewMode === 'bucket' && columnFilter && task.category_bucket !== columnFilter) return false;
    return true;
  });

  const getTasksByColumn = (columnId) => {
    if (viewMode === 'status') {
      // For status view, filter by status field
      // Map 'open', 'pending', null to 'todo' for backward compatibility
      return filteredTasks.filter(task => {
        const taskStatus = task.status || 'todo';
        if (columnId === 'todo') {
          return ['todo', 'open', 'pending', null, undefined, ''].includes(taskStatus);
        }
        return taskStatus === columnId;
      });
    }
    return filteredTasks.filter(task => task.category_bucket === columnId);
  };
  
  // Show all columns when filtering by overdue, otherwise show filtered column
  const visibleColumns = (columnFilter && columnFilter !== 'overdue' && viewMode === 'bucket') 
    ? columns.filter(col => col.id === columnFilter) 
    : columns;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#10B981';
      default: return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading tasks...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: isMobile ? '16px' : '20px 32px' }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '12px' : '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: isMobile ? '1 1 100%' : 'auto' }}>
              <button
                onClick={() => navigate('/a-dashboard')}
                style={{
                  padding: '8px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ArrowLeft size={18} color="#6B7280" />
              </button>
              <div>
                <h1 style={{ color: '#0A0A0A', fontSize: isMobile ? '20px' : '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  {viewMode === 'status' ? 'Task Board' : 'To-Do Center'}
                </h1>
                {!isMobile && (
                  <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                    {viewMode === 'status' ? 'Drag and drop tasks between columns' : 'Manage your tasks and priorities'}
                  </p>
                )}
              </div>
            </div>
            
            {/* View Mode Toggle */}
            <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', padding: '4px', borderRadius: '8px' }}>
              <button
                onClick={() => setViewMode('status')}
                style={{
                  padding: '6px 12px',
                  background: viewMode === 'status' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'status' ? '#3B82F6' : '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'status' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                }}
              >
                📋 Board
              </button>
              <button
                onClick={() => setViewMode('bucket')}
                style={{
                  padding: '6px 12px',
                  background: viewMode === 'bucket' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'bucket' ? '#3B82F6' : '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'bucket' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                }}
              >
                📅 Planning
              </button>
            </div>
            <button
              onClick={() => {
                setFormData({
                  title: '',
                  description: '',
                  priority: 'medium',
                  category_bucket: 'backlog',
                  due_date: '',
                  executive_id: '',
                  tags: []
                });
                setError('');
                setShowCreateModal(true);
              }}
              style={{
                padding: isMobile ? '10px 14px' : '10px 16px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flex: isMobile ? '1 1 100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              <Plus size={18} />
              New Task
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: isMobile ? '100%' : '300px' }}>
              <Search size={18} color="#9CA3AF" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#FFFFFF',
                flex: isMobile ? '1' : 'auto'
              }}
            >
              <option value="">All Priorities</option>
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' }}>
        {/* Column Filter Indicator */}
        {columnFilter && (
          <div style={{ 
            padding: '12px 16px', 
            background: columnFilter === 'overdue' ? '#FEF2F2' : '#EFF6FF', 
            border: columnFilter === 'overdue' ? '1px solid #FCA5A5' : '1px solid #DBEAFE',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: columnFilter === 'overdue' ? '#DC2626' : '#3B82F6', fontSize: '14px', fontWeight: '500' }}>
              Showing: {columnFilter === 'overdue' ? 'Overdue' : columns.find(c => c.id === columnFilter)?.label || columnFilter} tasks only
            </span>
            <button
              onClick={() => setColumnFilter('')}
              style={{
                padding: '6px 12px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Show All Columns
            </button>
          </div>
        )}
        
        {/* Desktop View - Kanban with Drag & Drop */}
        {!isMobile ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div style={{ display: 'flex', gap: '16px', overflowX: 'auto' }}>
              {visibleColumns.map(column => {
                const columnTasks = getTasksByColumn(column.id);
                return (
                  <div key={column.id} style={{ minWidth: '280px', flex: '1' }}>
                    <div style={{
                      background: '#FFFFFF',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      overflow: 'hidden'
                    }}>
                      {/* Column Header */}
                      <div style={{
                        padding: '16px',
                        borderBottom: '1px solid #E5E7EB',
                        background: '#F9FAFB'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {viewMode === 'status' && column.icon ? (
                              <span style={{ fontSize: '16px' }}>{column.icon}</span>
                            ) : (
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: column.color
                              }} />
                            )}
                            <h3 style={{ color: '#374151', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                              {column.label}
                            </h3>
                          </div>
                          <span style={{
                            background: column.id === 'completed' ? '#D1FAE5' : '#E5E7EB',
                            color: column.id === 'completed' ? '#059669' : '#6B7280',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {columnTasks.length}
                          </span>
                        </div>
                      </div>

                      {/* Tasks - Droppable */}
                      <Droppable droppableId={column.id}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                              padding: '12px',
                              minHeight: '400px',
                              maxHeight: '600px',
                              overflowY: 'auto',
                              background: snapshot.isDraggingOver ? '#F3F4F6' : 'transparent',
                              transition: 'background 0.2s'
                            }}
                          >
                            {columnTasks.length === 0 ? (
                              <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', marginTop: '24px' }}>
                                No tasks yet
                              </p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {columnTasks.map((task, index) => (
                                  <Draggable key={task.id} draggableId={task.id} index={index}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                          ...provided.draggableProps.style,
                                          background: snapshot.isDragging ? '#EFF6FF' : '#FFFFFF',
                                          border: snapshot.isDragging ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                                          borderRadius: '8px',
                                          padding: '12px',
                                          cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                          boxShadow: snapshot.isDragging ? '0 4px 12px rgba(59, 130, 246, 0.15)' : 'none',
                                          transition: 'box-shadow 0.2s'
                                        }}
                                        onClick={(e) => {
                                          // Prevent opening modal when dragging
                                          if (!snapshot.isDragging) {
                                            setSelectedTask(task);
                                            setFormData({
                                              title: task.title,
                                              description: task.description || '',
                                              notes: task.notes || [],
                                              priority: task.priority,
                                              category_bucket: task.category_bucket,
                                              due_date: task.due_date ? task.due_date.split('T')[0] : '',
                                              executive_id: task.executive_id || '',
                                              tags: task.tags || []
                                            });
                                            setNewNoteText('');
                                            setError('');
                                            setShowEditModal(true);
                                          }
                                        }}
                                      >
                              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <div style={{ flex: 1 }}>
                                  <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                                    {task.title}
                                  </p>
                                  {task.description && (
                                    <p style={{ color: '#6B7280', fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
                                      {task.description.substring(0, 60)}{task.description.length > 60 ? '...' : ''}
                                    </p>
                                  )}
                                </div>
                                <Flag
                                  size={14}
                                  color={getPriorityColor(task.priority)}
                                  fill={getPriorityColor(task.priority)}
                                />
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                                {task.due_date && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Calendar size={12} color="#9CA3AF" />
                                    <span style={{ color: '#6B7280', fontSize: '12px' }}>
                                      {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                {task.notes && task.notes.length > 0 && (
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '3px',
                                    background: '#FEF3C7',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    <span style={{ fontSize: '10px' }}>📝</span>
                                    <span style={{ color: '#92400E', fontSize: '11px', fontWeight: '500' }}>
                                      {task.notes.length}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Status selector */}
                              <div style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
                                {['open', 'in_progress', 'completed'].map(status => (
                                  <button
                                    key={status}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(task.id, status);
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      fontSize: '11px',
                                      border: task.status === status ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                                      background: task.status === status ? '#EFF6FF' : '#FFFFFF',
                                      color: task.status === status ? '#3B82F6' : '#6B7280',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: task.status === status ? '500' : '400'
                                    }}
                                  >
                                    {status.replace('_', ' ')}
                                  </button>
                                ))}
                              </div>
                            </div>
                                    )}
                                  </Draggable>
                                ))}
                              </div>
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        ) : (
          /* Mobile View - Accordion Style */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {visibleColumns.map(column => {
              const columnTasks = getTasksByColumn(column.id);
              const isExpanded = expandedColumn === column.id;
              
              return (
                <div
                  key={column.id}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden'
                  }}
                >
                  {/* Accordion Header */}
                  <div
                    onClick={() => setExpandedColumn(isExpanded ? null : column.id)}
                    style={{
                      padding: '14px 16px',
                      background: '#F9FAFB',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: isExpanded ? '1px solid #E5E7EB' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {viewMode === 'status' && column.icon ? (
                        <span style={{ fontSize: '16px' }}>{column.icon}</span>
                      ) : (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: column.color
                        }} />
                      )}
                      <h3 style={{ color: '#374151', fontSize: '15px', fontWeight: '600', margin: 0 }}>
                        {column.label}
                      </h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        background: column.id === 'completed' ? '#D1FAE5' : '#E5E7EB',
                        color: column.id === 'completed' ? '#059669' : '#6B7280',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {columnTasks.length}
                      </span>
                      <span style={{ color: '#6B7280', fontSize: '18px' }}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </div>
                  </div>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <div style={{ padding: '12px' }}>
                      {columnTasks.length === 0 ? (
                        <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
                          No tasks yet
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {columnTasks.map(task => (
                            <div
                              key={task.id}
                              style={{
                                background: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '12px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setSelectedTask(task);
                                setFormData({
                                  title: task.title,
                                  description: task.description || '',
                                  notes: task.notes || [],
                                  priority: task.priority,
                                  category_bucket: task.category_bucket,
                                  due_date: task.due_date ? task.due_date.split('T')[0] : '',
                                  executive_id: task.executive_id || '',
                                  tags: task.tags || []
                                });
                                setNewNoteText('');
                                setError('');
                                setShowEditModal(true);
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <div style={{ flex: 1 }}>
                                  <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                                    {task.title}
                                  </p>
                                  {task.description && (
                                    <p style={{ color: '#6B7280', fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
                                      {task.description.substring(0, 80)}{task.description.length > 80 ? '...' : ''}
                                    </p>
                                  )}
                                </div>
                                <Flag
                                  size={14}
                                  color={getPriorityColor(task.priority)}
                                  fill={getPriorityColor(task.priority)}
                                />
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                                {task.due_date && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Calendar size={12} color="#9CA3AF" />
                                    <span style={{ color: '#6B7280', fontSize: '12px' }}>
                                      {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                {task.notes && task.notes.length > 0 && (
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '3px',
                                    background: '#FEF3C7',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    <span style={{ fontSize: '10px' }}>📝</span>
                                    <span style={{ color: '#92400E', fontSize: '11px', fontWeight: '500' }}>
                                      {task.notes.length}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Status selector */}
                              <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {['open', 'in_progress', 'completed'].map(status => (
                                  <button
                                    key={status}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(task.id, status);
                                    }}
                                    style={{
                                      padding: '6px 10px',
                                      fontSize: '11px',
                                      border: task.status === status ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                                      background: task.status === status ? '#EFF6FF' : '#FFFFFF',
                                      color: task.status === status ? '#3B82F6' : '#6B7280',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: task.status === status ? '500' : '400'
                                    }}
                                  >
                                    {status.replace('_', ' ')}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #E5E7EB',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
              Create New Task
            </h3>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Task title"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add details..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Column
                  </label>
                  <select
                    value={formData.category_bucket}
                    onChange={(e) => setFormData({ ...formData, category_bucket: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="today">Today</option>
                    <option value="tomorrow">Tomorrow</option>
                    <option value="this_week">This Week</option>
                    <option value="next_week">Next Week</option>
                    <option value="backlog">Backlog</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {executives.length > 0 && (
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Related Executive
                  </label>
                  <select
                    value={formData.executive_id}
                    onChange={(e) => setFormData({ ...formData, executive_id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">None</option>
                    {executives.map(exec => (
                      <option key={exec.id} value={exec.id}>{exec.full_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #E5E7EB',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Edit Task
              </h3>
              <button
                onClick={handleDeleteClick}
                style={{
                  padding: '6px 12px',
                  background: '#FEE2E2',
                  color: '#DC2626',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                    Column
                  </label>
                  <select
                    value={formData.category_bucket}
                    onChange={(e) => setFormData({ ...formData, category_bucket: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="today">Today</option>
                    <option value="tomorrow">Tomorrow</option>
                    <option value="this_week">This Week</option>
                    <option value="next_week">Next Week</option>
                    <option value="backlog">Backlog</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Notes Section - Trello Style */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '8px' }}>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '10px' }}>
                  📝 Notes ({formData.notes?.length || 0})
                </label>
                
                {/* Existing Notes */}
                {formData.notes && formData.notes.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                    {formData.notes.map((note, index) => (
                      <div key={note.id || index} style={{
                        background: '#FFFBEB',
                        border: '1px solid #FDE68A',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        position: 'relative'
                      }}>
                        <p style={{ margin: 0, fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                          {note.text}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                            {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                          </span>
                          <button
                            onClick={() => {
                              const updatedNotes = formData.notes.filter((_, i) => i !== index);
                              setFormData({ ...formData, notes: updatedNotes });
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              fontSize: '11px',
                              cursor: 'pointer',
                              padding: '2px 6px'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Note */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Add a note..."
                    rows={2}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newNoteText.trim()) {
                        const newNote = {
                          id: Date.now().toString(),
                          text: newNoteText.trim(),
                          created_at: new Date().toISOString()
                        };
                        setFormData({ 
                          ...formData, 
                          notes: [...(formData.notes || []), newNote] 
                        });
                        setNewNoteText('');
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#3B82F6',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      alignSelf: 'flex-start'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTask(null);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        {...CONFIRM_MESSAGES.DELETE_TASK}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TodoCenter;
